import { memo, useCallback, useEffect, useMemo, useState, useRef } from "react";
import { useStore } from "../store/globalState";
import { useTranslation } from "react-i18next";
import {
  usePreventReload,
  useKeyboardVisible,
} from "lola-framework-ui-test/src/hooks";
import {
  AuraLayout,
  Button,
  Close,
  DebitCardIcon,
  LabelInput,
  Layout,
  Navbar,
  useBlockScroll,
} from "lola-framework-ui-test";
import { IMaskInput } from "react-imask";
import IMask from "imask";
import { APP_ROUTES, flavorClassName, isFlavorB } from "../constants";
import {
  CARD_BRANDS,
  detectCardBrand,
  DEFAULT_ALLOWED_BRANDS,
  type CardBrandKey,
} from "../constants/card-brands";
import { useSessionStore } from "../store/useSessionStore";
import "../styles/cardpage.style.css";
import { useRouteWithToken } from "../hooks/useRouteWithToken";
import { useClaimLink } from "../hooks/useClaimLink";
import { NavbarBackChevron } from "../components/NavbarBackChevron";
import { useNavigate } from "react-router-dom";
import { safeCapture } from "../utils/posthog";
import { RainbowWrapper } from "../components/RainbowWrapper";

export interface CardStepFields {
  cardNumber: string;
  expiry: string;
  cardHolderName: string;
  cvv: string;
}

interface CardFormErrors {
  cardNumber?: string;
  expiry?: string;
  cardHolderName?: string;
  cvv?: string;
}

const isDesktop = () => window.matchMedia("(pointer: fine)").matches;

export const AddCardPage = memo(() => {
  useClaimLink();
  const { theme } = useStore();
  const { session } = useSessionStore();
  const { redirectTo } = useRouteWithToken();
  const { t } = useTranslation();
  const navigate = useNavigate();
  useBlockScroll();

  const allowedBrands = (session.data?.config?.allowedCardBrands ??
    DEFAULT_ALLOWED_BRANDS) as CardBrandKey[];
  const isUSOnly =
    (session.data?.config?.cardOriginPolicy ?? "us_only") === "us_only";

  const [form, setForm] = useState<CardStepFields>({
    cardNumber: "",
    expiry: "",
    cardHolderName: "",
    cvv: "",
  });
  const [errors, setErrors] = useState<CardFormErrors>({});
  const [focusedField, setFocusedField] = useState<keyof CardStepFields | null>(
    null
  );

  const viewedRef = useRef(false);
  useEffect(() => {
    if (!viewedRef.current) {
      viewedRef.current = true;
      safeCapture("ui.send.add_card.screen.view");
    }
  }, []);

  const detectedBrand = useMemo(
    () => detectCardBrand(form.cardNumber),
    [form.cardNumber]
  );

  const detectedBrandInfo =
    detectedBrand && allowedBrands.includes(detectedBrand)
      ? CARD_BRANDS[detectedBrand]
      : null;

  const formatList = useCallback(() => {
    const formatter = new Intl.ListFormat(
      session.data?.language?.toLocaleLowerCase(),
      {
        style: "long",
        type: "conjunction",
      }
    );

    const items = allowedBrands
      .map((key) => CARD_BRANDS[key]?.label)
      .filter(Boolean);
    return formatter.format(items);
  }, [session.data?.language, allowedBrands]);

  const brandsLabel = formatList();

  const { isKeyboardOpen } = useKeyboardVisible();
  const showFooter = isKeyboardOpen;
  const [showInfo, setShowInfo] = useState(showFooter);

  usePreventReload();

  useEffect(() => {
    let timer: ReturnType<typeof setTimeout> | undefined;

    if (!isDesktop()) {
      if (!showFooter) {
        timer = setTimeout(() => {
          setShowInfo(showFooter);
        }, 1000);
      } else {
        // eslint-disable-next-line react-hooks/set-state-in-effect
        setShowInfo(showFooter);
      }
    }

    return () => {
      clearTimeout(timer);
    };
  }, [showFooter]);

  const colorsConfig = theme?.whiteView;
  const {
    backgroundBtn,
    textColorBtn,
    title,
    buttonShowIcon,
    buttonSize,
  } = colorsConfig;

  const handleChange = (field: keyof CardStepFields, value: string) => {
    setForm((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: undefined }));
    }
  };

  const validate = (): CardFormErrors => {
    const errs: CardFormErrors = {};
    const cleaned = form.cardNumber.replace(/\s/g, "");
    if (!/^\d{13,19}$/.test(cleaned)) {
      errs.cardNumber = t("card.invalidNumber");
    } else {
      const brand = detectCardBrand(form.cardNumber);
      if (!brand || !allowedBrands.includes(brand)) {
        errs.cardNumber = t("card.brandNotAccepted");
      }
    }

    const [mm, yy] = form.expiry.split("/");
    if (!mm || !yy || !/^(0[1-9]|1[0-2])$/.test(mm) || !/^\d{2}$/.test(yy)) {
      errs.expiry = t("card.invalidExpiry");
    } else {
      const now = new Date();
      const currentMonth = now.getMonth() + 1;
      const currentYear = now.getFullYear() % 100;
      const expMonth = parseInt(mm, 10);
      const expYear = parseInt(yy, 10);
      if (
        expYear < currentYear ||
        (expYear === currentYear && expMonth < currentMonth)
      ) {
        errs.expiry = t("card.expiredCard", {
          defaultValue: "Card is expired",
        });
      }
    }

    if (!form.cardHolderName.trim())
      errs.cardHolderName = t("card.nameRequired");
    if (!/^\d{3,4}$/.test(form.cvv)) errs.cvv = t("card.invalidCvv");

    return errs;
  };

  const handleContinue = () => {
    safeCapture("ui.send.add_card.continue_button.click");
    const validationErrors = validate();
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      safeCapture("ux.send.add_card.form.error", {
        success: false,
        error_code: "CARD_VALIDATION",
        fields: Object.keys(validationErrors),
      });
      return;
    }

    redirectTo(APP_ROUTES.ADD_CARD_ADDRESS_PAGE, {
      state: { card: form },
    });
  };

  const inputStyle: React.CSSProperties = {
    width: "100%",
    padding: "12px 14px",
    borderRadius: "var(--lola-style-input-border-radius, 10px)",
    fontSize: "16px",
    outline: "none",
    boxSizing: "border-box",
    background: "transparent",
  };

  const errorStyle: React.CSSProperties = {
    color: "#dc251c",
    fontSize: "12px",
    marginTop: "4px",
  };

  // LabelInput applies `background`/`color` props AFTER labelStyle — must set them
  // explicitly or the kit defaults (#fff) create a "tape". Match the input fill
  // (--gradient-card), not the page background.
  const inputLabelBg = "var(--gradient-card)";
  const inputLabelColor = "var(--foreground)";

  return (
    <AuraLayout
      colorConfig={colorsConfig}
      className={`${flavorClassName}`}
    >
      <Layout.Header>
        <NavbarBackChevron onBackClick={() => navigate(-1)}>
          <Navbar
            align="center"
            color={title}
            title={isUSOnly ? t("card.addUS") : t("card.add")}
            onBackClick={() => navigate(-1)}
            icon={<Close size={20} colors={[title, title]} />}
            onIconClick={() => navigate(-1)}
          />
        </NavbarBackChevron>
      </Layout.Header>

      <Layout.Content>
        {isFlavorB && (
          <section className="cardpage-title-container">
            <section className="cardpage-title">
              <DebitCardIcon size={32} />
              <h2 className="foreground h2">
                {isUSOnly ? t("card.addDetailsUS") : t("card.addDetails")}
              </h2>
            </section>
            <p className="muted-foreground text-normal">
              {t("card.acceptedBrands", { brands: brandsLabel })}
            </p>
          </section>
        )}
        <div className="cardpage-content">
          {/*
            LabelInput is position:absolute with top:50% when inactive.
            Keep it inside `.input-field-shell` so the error message below
            does not grow the positioning context and pull the label down.
            When there is an error (or value/focus), keep isActive so the
            label stays on the border instead of overlapping the input.
          */}
          <div className="input-container-wrapper">
            <div className="input-field-shell">
              <LabelInput
                isActive={
                  focusedField === "cardHolderName" ||
                  !!form.cardHolderName ||
                  !!errors.cardHolderName
                }
                background={inputLabelBg}
                color={inputLabelColor}
              >
                {t("card.owner")}
              </LabelInput>
              <RainbowWrapper
                value={form.cardHolderName}
                error={errors.cardHolderName}
                isActive={focusedField === "cardHolderName"}
              >
                <input
                  type="text"
                  className="input-style"
                  inputMode="text"
                  aria-label={t("card.owner")}
                  value={form.cardHolderName}
                  onChange={(e) =>
                    handleChange("cardHolderName", e.target.value)
                  }
                  onFocus={() => setFocusedField("cardHolderName")}
                  onBlur={() => setFocusedField(null)}
                  style={inputStyle}
                  autoFocus
                />
              </RainbowWrapper>
            </div>
            {errors.cardHolderName && (
              <div style={errorStyle}>{errors.cardHolderName}</div>
            )}
          </div>

          <div className="input-container-wrapper">
            <div className="input-field-shell">
              <LabelInput
                isActive={
                  focusedField === "cardNumber" ||
                  !!form.cardNumber ||
                  !!errors.cardNumber
                }
                background={inputLabelBg}
                color={inputLabelColor}
              >
                {t("card.number")}
              </LabelInput>
              <RainbowWrapper
                value={form.cardNumber}
                error={errors.cardNumber}
                isActive={focusedField === "cardNumber"}
              >
                <div style={{ position: "relative" }}>
                  <IMaskInput
                    mask="0000 0000 0000 0000"
                    className="input-style"
                    value={form.cardNumber}
                    unmask={false}
                    onAccept={(value: string) =>
                      handleChange("cardNumber", value)
                    }
                    onFocus={() => setFocusedField("cardNumber")}
                    onBlur={() => setFocusedField(null)}
                    style={inputStyle}
                    inputMode="numeric"
                    aria-label={t("card.number")}
                  />
                  {detectedBrandInfo && (
                    <img
                      src={detectedBrandInfo.logo}
                      alt={t(detectedBrandInfo.altKey)}
                      style={{
                        position: "absolute",
                        right: 12,
                        top: "50%",
                        transform: "translateY(-50%) scale(.8)",
                        height: 24,
                      }}
                    />
                  )}
                </div>
              </RainbowWrapper>
            </div>
            {errors.cardNumber && (
              <div style={errorStyle}>{errors.cardNumber}</div>
            )}
          </div>

          <div className="cardpage-row">
            <div className="input-container-wrapper cardpage-row__field">
              <div className="input-field-shell">
                <LabelInput
                  isActive={
                    focusedField === "expiry" ||
                    !!form.expiry ||
                    !!errors.expiry
                  }
                  background={inputLabelBg}
                  color={inputLabelColor}
                >
                  {t("card.expiry")}
                </LabelInput>
                <RainbowWrapper
                  value={form.expiry}
                  error={errors.expiry}
                  isActive={focusedField === "expiry"}
                >
                  <IMaskInput
                    mask="MM{/}YY"
                    blocks={{
                      MM: {
                        mask: IMask.MaskedRange,
                        from: 1,
                        to: 12,
                        maxLength: 2,
                      },
                      YY: {
                        mask: IMask.MaskedRange,
                        from: 0,
                        to: 99,
                        maxLength: 2,
                      },
                    }}
                    placeholder={t("card.expiryPlaceholder", {
                      defaultValue: "MM/YY",
                    })}
                    className="input-style"
                    value={form.expiry}
                    onAccept={(value: string) => handleChange("expiry", value)}
                    onFocus={() => setFocusedField("expiry")}
                    onBlur={() => setFocusedField(null)}
                    style={inputStyle}
                    inputMode="numeric"
                    aria-label={t("card.expiry")}
                  />
                </RainbowWrapper>
              </div>
              {errors.expiry && <div style={errorStyle}>{errors.expiry}</div>}
            </div>
            <div className="input-container-wrapper cardpage-row__field">
              <div className="input-field-shell">
                <LabelInput
                  isActive={
                    focusedField === "cvv" || !!form.cvv || !!errors.cvv
                  }
                  background={inputLabelBg}
                  color={inputLabelColor}
                >
                  {t("card.cvvPlaceholder")}
                </LabelInput>
                <RainbowWrapper
                  value={form.cvv}
                  error={errors.cvv}
                  isActive={focusedField === "cvv"}
                >
                  <IMaskInput
                    mask="000[0]"
                    value={form.cvv}
                    className="input-style"
                    unmask
                    onAccept={(value: string) => handleChange("cvv", value)}
                    onFocus={() => setFocusedField("cvv")}
                    onBlur={() => setFocusedField(null)}
                    style={inputStyle}
                    inputMode="numeric"
                    aria-label={t("card.cvvPlaceholder", {
                      defaultValue: "CVV",
                    })}
                  />
                </RainbowWrapper>
              </div>
              {errors.cvv && <div style={errorStyle}>{errors.cvv}</div>}
            </div>
          </div>
        </div>
      </Layout.Content>
      <Layout.Footer>
        <section className="footer-section">
          <section
            className="valid-cards"
            style={{
              marginTop: "auto",
              position: "static",
            }}
          >
            {!showInfo && (
              <section className="valid-cards-container">
                <p className="footer-text muted-foreground">
                  {t("card.acceptedBrands", { brands: brandsLabel })}
                </p>
                <section className="valid-cards-logos">
                  {allowedBrands.map((key) => {
                    const brand = CARD_BRANDS[key];
                    if (!brand) return null;
                    return (
                      <img
                        key={key}
                        className="card-logos"
                        src={brand.logo}
                        alt={t(brand.altKey)}
                      />
                    );
                  })}
                </section>
                <p className="footer-text muted-foreground">
                  {t("card.disclaimer")}
                </p>
              </section>
            )}
          </section>
          <Button
            color={textColorBtn}
            background={backgroundBtn}
            showIcon={buttonShowIcon}
            size={buttonSize ?? "small"}
            onClick={handleContinue}
          >
            {t("common.continue")}
          </Button>
        </section>
      </Layout.Footer>
    </AuraLayout>
  );
});
